import { SYNTHESISE_AI_TASKS } from '../../../../src/commonConstants';

export type SynthesiseTaskId = keyof typeof SYNTHESISE_AI_TASKS;

export type Stage1ContextVariant =
	| 'context-1-4'
	| 'context-5-9'
	| 'context-10-plus'
	| 'text-short'
	| 'text-long'
	| 'mixed-1-3'
	| 'mixed-4-plus'
	| 'visual-only'
	| 'invalid';

export type Stage1ContextInput = {
	role?: string;
	src?: string;
	label?: string;
	value?: string;
};

export type Stage1Context = {
	primaryText: string;
	primaryVisualSrc?: string;
	primaryRole?: string;
	additionalInputs: Stage1ContextInput[];
};

const wordCount = (value: string): number =>
	value.trim() ? value.trim().split(/\s+/).length : 0;

/**
 * Recreates the historical Synthesise input-shape variants without coupling
 * output visibility to the legacy multi-call generation pipeline.
 */
export const classifyStage1Context = (context: Stage1Context): Stage1ContextVariant => {
	const words = wordCount(context.primaryText);
	const hasText = words > 0;
	const contextItemCount = context.additionalInputs.length;
	const hasVisualReference = Boolean(
		context.primaryVisualSrc || context.additionalInputs.some((input) => input.src)
	);
	const hasNonVisualContext = context.additionalInputs.some(
		(input) => input.role !== 'visual-reference' && !input.src
	);

	// Unlike the old image-first branch, only genuinely visual-only recipes are
	// restricted to media outputs. A visual supplied alongside written context
	// remains valid context for text generation.
	if (hasVisualReference && !hasText && !hasNonVisualContext) return 'visual-only';
	if (hasText && contextItemCount > 0) {
		return contextItemCount <= 3 ? 'mixed-1-3' : 'mixed-4-plus';
	}
	if (hasText) return words <= 3 ? 'text-short' : 'text-long';
	if (contextItemCount > 0) {
		if (contextItemCount <= 4) return 'context-1-4';
		if (contextItemCount <= 9) return 'context-5-9';
		return 'context-10-plus';
	}
	return 'invalid';
};

const HIDDEN_TASKS: Record<Stage1ContextVariant, ReadonlySet<SynthesiseTaskId>> = {
	'context-1-4': new Set(['Summary', 'Audience']),
	'context-5-9': new Set(['Summary']),
	'context-10-plus': new Set(['Summary']),
	'text-short': new Set(['Unite', 'Audience']),
	'text-long': new Set(['Unite']),
	'mixed-1-3': new Set(['Summary', 'PESTEL', 'Extract', 'Audience']),
	'mixed-4-plus': new Set(['Summary', 'PESTEL', 'Extract']),
	'visual-only': new Set(),
	invalid: new Set(),
};

export const filterStage1Tasks = (
	allTaskIds: SynthesiseTaskId[],
	variant: Stage1ContextVariant
): SynthesiseTaskId[] => {
	if (variant === 'visual-only') {
		return allTaskIds.filter((taskId) => taskId === 'Image' || taskId === 'Video');
	}
	const hidden = HIDDEN_TASKS[variant];
	return allTaskIds.filter((taskId) => !hidden.has(taskId));
};

const SOURCE_TEXT_WORD_THRESHOLD = 80;

const explicitlyRequestedTasks = (text: string): Set<SynthesiseTaskId> => {
	const normalized = text.toLocaleLowerCase().replace(/[-_]+/g, ' ');
	const requested = new Set<SynthesiseTaskId>();
	if (/\b(summar(?:y|ise|ize)|recap)\b/.test(normalized)) requested.add('Summary');
	if (/\b(extract|key points?|essence)\b/.test(normalized)) requested.add('Extract');
	if (/\b(unite|combine|common idea|synthesi[sz]e)\b/.test(normalized)) requested.add('Unite');
	if (/\b(target audience|audience|personas?|target market)\b/.test(normalized)) {
		requested.add('Audience');
	}
	if (/\b(pestel|macro environment|external factors?)\b/.test(normalized)) {
		requested.add('PESTEL');
	}
	return requested;
};

const hasRole = (context: Stage1Context, roles: ReadonlySet<string>): boolean =>
	roles.has(context.primaryRole || '') ||
	context.additionalInputs.some((input) => roles.has(input.role || ''));

const hasSubstantialSourceText = (context: Stage1Context): boolean =>
	wordCount(context.primaryText) >= SOURCE_TEXT_WORD_THRESHOLD ||
	context.additionalInputs.some(
		(input) => wordCount(input.value || '') >= SOURCE_TEXT_WORD_THRESHOLD
	);

/**
 * Stage 1.5 keeps historical filtering predictable but restores outputs when
 * existing recipe roles or explicit user wording provide a clear capability
 * signal. It deliberately does not use a model or hide additional tasks.
 */
export const filterStage15Tasks = (
	allTaskIds: SynthesiseTaskId[],
	context: Stage1Context
): SynthesiseTaskId[] => {
	const variant = classifyStage1Context(context);
	const visible = new Set(filterStage1Tasks(allTaskIds, variant));
	const hasSourceMaterial =
		hasRole(context, new Set(['evidence'])) || hasSubstantialSourceText(context);
	const hasDefinedSubject = hasRole(context, new Set(['primary-concept', 'brief']));
	const nonVisualContentCount =
		(wordCount(context.primaryText) > 0 ? 1 : 0) +
		context.additionalInputs.filter(
			(input) => input.role !== 'visual-reference' && !input.src
		).length;

	if (hasSourceMaterial) {
		visible.add('Summary');
		visible.add('Extract');
		visible.add('PESTEL');
	}
	if (hasDefinedSubject) visible.add('Audience');
	if (nonVisualContentCount >= 2) visible.add('Unite');
	for (const task of explicitlyRequestedTasks(context.primaryText)) visible.add(task);

	return allTaskIds.filter((taskId) => visible.has(taskId));
};

